import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { nanoid } from 'nanoid';
import { spacing, color, border } from '@metro-ui/tokens/js';
import { fontStack } from '../utils/fontStack';
import { visuallyHidden } from '../utils/visuallyHidden';
import { onChangeValueHandler, } from '../shared/eventHandler';
import { Icon } from './components/Icon';
import { Input } from './components/Input';
import { Label } from '../Label';
var _a = React.createContext({
    hasGroup: false,
    name: 'radio-group',
    value: '',
    onChange: function (e, val) {
        // eslint-disable-next-line no-console
        console.info("You need to provide an onChange callback when using a RadioButtonGroup.\n      The callback will receive the native ChangeEvent and the RadioButtons value, in this case:", e, val);
    },
}), Provider = _a.Provider, Consumer = _a.Consumer;
var getOnChangeHandler = function (hasGroup, onGroupChange, onChange, value) {
    if (hasGroup)
        return onChangeValueHandler(onGroupChange, value);
    if (onChange !== undefined)
        return onChangeValueHandler(onChange, value);
    return onChange;
};
export var RadioButton = styled(React.forwardRef(function (_a, ref) {
    var id = _a.id, name = _a.name, value = _a.value, isChecked = _a.isChecked, isDisabled = _a.isDisabled, onChange = _a.onChange, className = _a.className, children = _a.children, htmlAttributes = __rest(_a, ["id", "name", "value", "isChecked", "isDisabled", "onChange", "className", "children"]);
    return (React.createElement(Consumer, null, function (_a) {
        var hasGroup = _a.hasGroup, groupName = _a.name, currentGroupValue = _a.value, groupOnChange = _a.onChange;
        var checked = hasGroup ? value === currentGroupValue : isChecked;
        return (React.createElement(Label, { className: className, htmlFor: id, "data-mui-radio": "", "data-active": checked, isDisabled: isDisabled },
            React.createElement(Input, __assign({}, htmlAttributes, { "data-mui-radio-input": "", type: "radio", ref: ref, onChange: getOnChangeHandler(hasGroup, groupOnChange, onChange, value), disabled: isDisabled, name: hasGroup ? groupName : name }, {
                id: id,
                value: value,
                checked: checked,
            })),
            React.createElement(Icon, { "data-mui-radio-icon": "" }),
            !!children && React.createElement("span", { "data-mui-radio-label": "" }, children)));
    }));
}))(function () { return css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    ", "\n    display: flex;\n    cursor: pointer;\n\n    &:hover {\n      [data-mui-radio-input]:not(:disabled) + [data-mui-radio-icon] {\n        border-color: ", ";\n      }\n    }\n  "], ["\n    ", "\n    display: flex;\n    cursor: pointer;\n\n    &:hover {\n      [data-mui-radio-input]:not(:disabled) + [data-mui-radio-icon] {\n        border-color: ", ";\n      }\n    }\n  "])), fontStack, color.interaction); });
RadioButton.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    isChecked: PropTypes.bool,
    isDisabled: PropTypes.bool,
    onChange: PropTypes.func,
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
var VisuallyHidden = styled.span(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), visuallyHidden);
export var RadioButtonGroup = styled(React.forwardRef(function (_a, ref) {
    var name = _a.name, _b = _a.labelId, labelId = _b === void 0 ? nanoid() : _b, label = _a.label, _c = _a.value, value = _c === void 0 ? '' : _c, onChange = _a.onChange, className = _a.className, children = _a.children;
    return (React.createElement(Provider, { value: { hasGroup: true, name: name, value: value, onChange: onChange } },
        React.createElement("div", { className: className, role: "radiogroup", "aria-labelledby": labelId, ref: ref },
            React.createElement(VisuallyHidden, { "aria-hidden": "true", id: labelId }, label),
            children)));
}))(function (_a) {
    var hasBorder = _a.hasBorder;
    return css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    ", "\n  "], ["\n    ", "\n  "])), hasBorder && css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      border-radius: ", ";\n      box-shadow: inset 0 0 0 1px ", ";\n\n      overflow: hidden;\n\n      & [data-mui-radio] {\n        padding: ", ";\n        box-shadow: 0 1px 0 0 ", ";\n      }\n\n      & [data-active='true'] {\n        box-shadow: inset 0 0 0 2px ", ";\n        z-index: 1;\n      }\n    "], ["\n      border-radius: ", ";\n      box-shadow: inset 0 0 0 1px ", ";\n\n      overflow: hidden;\n\n      & [data-mui-radio] {\n        padding: ", ";\n        box-shadow: 0 1px 0 0 ", ";\n      }\n\n      & [data-active='true'] {\n        box-shadow: inset 0 0 0 2px ", ";\n        z-index: 1;\n      }\n    "])), border.radius.small, color.palette.grey.tint[80], spacing.medium, color.palette.grey.tint[80], color.focused));
});
RadioButtonGroup.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    labelId: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    hasBorder: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=RadioButton.js.map